<template>
  <div class="space-y-4">
    <div class="lg:hidden">
      <button class="acc-btn" type="button" @click="mobileNavOpen = true">
        <i class="fas fa-bars" aria-hidden="true"></i>
        <span>Розділи налаштувань</span>
      </button>
    </div>

    <div v-if="mobileNavOpen" class="fixed inset-0 z-[160] lg:hidden">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="mobileNavOpen = false" />
      <aside class="absolute inset-y-0 left-0 w-[85%] max-w-sm acc-glass rounded-r-2xl border-r border-white/15 p-4 overflow-auto">
        <div class="flex items-center justify-between mb-3">
          <div class="font-extrabold">Налаштування</div>
          <button class="acc-btn !px-2 !py-1" type="button" @click="mobileNavOpen = false">✕</button>
        </div>
        <div class="space-y-2">
          <button
            v-for="item in navItems"
            :key="`m-${item.id}`"
            class="acc-btn w-full justify-start"
            :class="{ 'acc-settings-nav-active': currentSection === item.id }"
            type="button"
            @click="scrollToSection(item.id)"
          >
            <i :class="item.icon" aria-hidden="true"></i>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </aside>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:items-start lg:grid-cols-[260px,minmax(0,1fr)] xl:grid-cols-[280px,minmax(0,1fr)]">
      <aside class="hidden lg:block min-w-0 lg:sticky lg:top-24 lg:self-start h-fit">
        <div class="acc-glass rounded-2xl border border-white/10 p-3">
          <div class="px-2 pb-2 text-[11px] font-extrabold uppercase tracking-widest text-slate-500 dark:text-white/40">
            Налаштування
          </div>
          <div class="space-y-2">
            <button
              v-for="item in navItems"
              :key="item.id"
              class="acc-btn w-full justify-start"
              :class="{ 'acc-settings-nav-active': currentSection === item.id }"
              type="button"
              @click="scrollToSection(item.id)"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </button>
          </div>
        </div>
      </aside>

      <div class="space-y-5 min-w-0">
        <section id="settings-profile" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsProfile :profile="profile" :is-owner="isOwner" @profile-updated="forwardProfileUpdated" />
        </section>

        <section id="settings-design" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsDesign :profile="profile" :is-owner="isOwner" @profile-updated="forwardProfileUpdated" />
        </section>

        <section id="settings-privacy" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsPrivacy :profile="profile" />
        </section>

        <section id="settings-notifications" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsNotifications :profile="profile" />
        </section>

        <section id="settings-security" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsSecurity />
        </section>

        <section id="settings-account" class="scroll-mt-24 min-w-0 overflow-hidden">
          <SettingsAccount :busy="sessionBusy" :requested-at="profile?.account_deletion_requested_at" :scheduled-for="profile?.account_deletion_scheduled_for" :can-cancel="profile?.account_deletion_can_cancel === true && !cancelExpired" @delete-account="deleteAccount" @cancel-deletion="cancelDeletion" />
          <p v-if="cancelError" class="mt-2 text-sm text-red-700 dark:text-red-200" role="alert">{{ cancelError }}</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import SettingsProfile from './SettingsProfile.vue';
import SettingsDesign from './SettingsDesign.vue';
import SettingsSecurity from './SettingsSecurity.vue';
import SettingsNotifications from './SettingsNotifications.vue';
import SettingsPrivacy from './SettingsPrivacy.vue';
import SettingsAccount from './SettingsAccount.vue';

export default {
  name: 'SettingsUnified',
  components: {
    SettingsProfile,
    SettingsDesign,
    SettingsSecurity,
    SettingsNotifications,
    SettingsPrivacy,
    SettingsAccount,
  },
  props: {
    profile: { type: Object, default: null },
    dashboard: { type: Object, default: null },
    isOwner: { type: Boolean, default: false },
  },
  data() {
    return {
      mobileNavOpen: false,
      sessionBusy: false,
      deleteConfirmPending: false,
      cancelExpired: false,
      cancelError: '',
      currentSection: 'settings-profile',
      sectionObserver: null,
    };
  },
  computed: {
    navItems() {
      return [
        { id: 'settings-profile', label: 'Профіль', icon: 'fas fa-user' },
        { id: 'settings-design', label: 'Оформлення', icon: 'fas fa-palette' },
        { id: 'settings-privacy', label: 'Приватність', icon: 'fas fa-user-shield' },
        { id: 'settings-notifications', label: 'Сповіщення', icon: 'fas fa-bell' },
        { id: 'settings-security', label: 'Безпека', icon: 'fas fa-shield-alt' },
        { id: 'settings-account', label: 'Акаунт і дані', icon: 'fas fa-database' },
      ];
    },
  },
  methods: {
    forwardProfileUpdated(payload) {
      this.$emit('profile-updated', payload);
    },
    isKnownSection(id) {
      return this.navItems.some((item) => item.id === id);
    },
    sectionFromLocationHash() {
      const hash = window.location.hash ? window.location.hash.slice(1) : '';
      return this.isKnownSection(hash) ? hash : 'settings-profile';
    },
    applyLocationHash() {
      const id = this.sectionFromLocationHash();
      this.currentSection = id;
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
    },
    handleHashChange() {
      this.applyLocationHash();
    },
    scrollToSection(id) {
      if (!this.isKnownSection(id)) return;
      this.currentSection = id;
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState({}, '', `#${id}`);
      this.mobileNavOpen = false;
    },
    lifecycleRequestOptions() {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      return {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'X-CSRF-TOKEN': token, Accept: 'application/json' },
      };
    },
    applyLifecycleState(profile) {
      if (!profile || typeof profile !== 'object') {
        throw new Error('Lifecycle response did not include authoritative profile state.');
      }
      this.$emit('profile-updated', {
        account_deletion_requested_at: profile.account_deletion_requested_at ?? null,
        account_deletion_scheduled_for: profile.account_deletion_scheduled_for ?? null,
        account_deletion_can_cancel: profile.account_deletion_can_cancel === true,
      });
    },
    showLifecycleSuccess(message) {
      const toast = window.alertModalInstance?.$refs?.modal;
      if (toast?.alert) {
        toast.alert(message, 'Успіх', 'success');
        return;
      }
      window.showNotification?.(message, 'success');
    },
    async deleteAccount() {
      if (this.sessionBusy || this.deleteConfirmPending) return;
      this.deleteConfirmPending = true;
      try {
        const confirmed = await window.confirm('Розпочати 30-денний період видалення акаунта? Протягом 30 днів можна скасувати запит. Цей сеанс залишиться активним, інші сеанси буде закрито.');
        if (!confirmed || this.sessionBusy) return;
        this.sessionBusy = true;
        this.cancelError = '';
        const response = await window.fetch('/profile', this.lifecycleRequestOptions());
        if (!response.ok) {
          throw new Error('Account deletion request failed.');
        }
        const payload = await response.json();
        this.applyLifecycleState(payload.profile);
        this.showLifecycleSuccess(payload.message || 'Видалення облікового запису заплановано. Ви можете скасувати його протягом 30 днів.');
      } catch (error) {
        this.cancelError = 'Не вдалося запланувати видалення. Оновіть сторінку та спробуйте ще раз.';
      } finally {
        this.sessionBusy = false;
        this.deleteConfirmPending = false;
      }
    },
    async cancelDeletion() {
      if (this.sessionBusy || !this.profile?.account_deletion_scheduled_for
        || this.profile?.account_deletion_can_cancel !== true || this.cancelExpired) return;
      this.sessionBusy = true;
      this.cancelError = '';
      try {
        const response = await window.fetch('/profile/deletion-request', this.lifecycleRequestOptions());
        if (response.status === 409) {
          this.cancelExpired = true;
          window.location.reload();
          return;
        }
        if (!response.ok) {
          this.cancelError = 'Не вдалося скасувати видалення. Оновіть сторінку та спробуйте ще раз.';
          this.sessionBusy = false;
          return;
        }
        const payload = await response.json();
        this.applyLifecycleState(payload.profile);
        this.showLifecycleSuccess(payload.message || 'Видалення облікового запису скасовано.');
        this.sessionBusy = false;
      } catch (error) {
        this.cancelError = 'Не вдалося скасувати видалення. Оновіть сторінку та спробуйте ще раз.';
        this.sessionBusy = false;
      }
    },
    initSectionObserver() {
      const ids = this.navItems.map((item) => item.id);
      const targets = ids
        .map((id) => document.getElementById(id))
        .filter(Boolean);
      if (!targets.length) return;

      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (!visible.length) return;
          const top = visible[0].target?.id;
          if (top) this.currentSection = top;
        },
        {
          root: null,
          rootMargin: '-90px 0px -55% 0px',
          threshold: [0.15, 0.35, 0.6],
        },
      );

      targets.forEach((el) => this.sectionObserver.observe(el));
    },
  },
  mounted() {
    window.addEventListener('hashchange', this.handleHashChange);
    this.$nextTick(() => {
      this.applyLocationHash();
      this.initSectionObserver();
    });
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.handleHashChange);
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
  },
};
</script>

<style scoped>
.acc-settings-nav-active {
  border-color: var(--acc-accent, #A84F1D) !important;
  box-shadow: 0 0 0 1px var(--acc-accent, #A84F1D) inset;
  color: var(--acc-accent-foreground-light, #873B14) !important;
}
</style>
